// The sync engine: sends a form on this phone to the server (round 42).
//
// The steps, in order, each remembered on the record the moment it lands:
//
//   1. create the draft          (idempotent: the phone names the id)
//   2. upload each photo         (idempotent: the phone names each file id)
//   3. remove photos taken off   (a 404 means already gone)
//   4. save the answers          (a PATCH of the whole set; safe to repeat)
//   5. submit, if Submit was pressed
//
// Every step can therefore be resent after a lost reply without doing
// anything twice, which is the whole of what makes a queue safe on a bad
// signal. A step that fails stops the run; the next run starts from the
// first step not yet recorded.
//
// What stops a run, and what the phone makes of it:
//
//   no signal (or the server mid-restart)  'offline'     try again later
//   the session has ended                  'signed_out'  keep, sign in to send
//   the server said no                     'refused'     back to a draft, its
//                                                        own words on it
//   "already submitted"                    'sent'        an earlier submit
//                                                        landed; its reply
//                                                        was what got lost
import { ApiError } from '../api';
import { formFill, submitProblemsFrom, uploadSubmissionFile, type Submission } from '../formsApi';
import { offlineStore, type OfflineStore } from './store';
import type { LocalForm } from './localForm';

export type SyncOutcome =
  | { kind: 'sent'; submission: Submission | null }
  | { kind: 'saved' }
  | { kind: 'offline' }
  | { kind: 'signed_out' }
  | { kind: 'refused' }
  | { kind: 'missing' };

/** The server calls, as a seam: the tests hand in their own. */
export interface SyncApi {
  start: typeof formFill.start;
  upload: typeof uploadSubmissionFile;
  deleteFile: typeof formFill.deleteFile;
  save: typeof formFill.save;
  submit: typeof formFill.submit;
}

const liveApi: SyncApi = {
  start: formFill.start,
  upload: uploadSubmissionFile,
  deleteFile: formFill.deleteFile,
  save: formFill.save,
  submit: formFill.submit,
};

// --- Change notices ------------------------------------------------------
//
// The Forms page and the fill page redraw when a record changes underneath
// them (a step landed, a refusal arrived). One tab, one list of listeners.

type Listener = (formId: string) => void;
const listeners = new Set<Listener>();

export function onFormChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

export function notifyFormChange(formId: string): void {
  for (const listener of listeners) listener(formId);
}

// --- One run -------------------------------------------------------------

/** Deletes a form and its bytes from this phone: sent, or thrown away. */
export async function forgetForm(store: OfflineStore, form: LocalForm): Promise<void> {
  await store.deleteForm(form.id);
  for (const file of form.files) await store.deleteBlob(file.id);
  notifyFormChange(form.id);
}

function isNetwork(error: unknown): boolean {
  // Status 0 is `request`'s "could not reach Dayboard". A 502/503/504 is the
  // server between processes (the first request after a reload can 502), not
  // an answer about this form.
  return (
    error instanceof ApiError &&
    (error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504)
  );
}

function isSignedOut(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/** 409 on a step AFTER create: the form was submitted, by us or elsewhere. */
function isAlreadySubmitted(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

async function record(
  store: OfflineStore,
  id: string,
  change: (form: LocalForm) => LocalForm
): Promise<LocalForm | undefined> {
  const next = await store.updateForm(id, (form) => ({
    ...change(form),
    updatedAt: new Date().toISOString(),
  }));
  notifyFormChange(id);
  return next;
}

async function runOnce(id: string, store: OfflineStore, api: SyncApi): Promise<SyncOutcome> {
  let form = await store.getForm(id);
  if (!form) return { kind: 'missing' };

  let step: 'create' | 'after-create' = 'create';
  try {
    // 1. Create.
    if (!form.created) {
      await api.start({
        id: form.id,
        templateId: form.templateId,
        ...(form.linkKind === 'asset'
          ? { resourceId: form.resourceId }
          : { jobId: form.jobId }),
        ...(form.startedOnPhone ? { versionId: form.versionId } : {}),
      });
      form = (await record(store, id, (f) => ({ ...f, created: true, problem: null })))!;
    }
    step = 'after-create';

    // 2. Uploads. A photo whose bytes this phone no longer has (storage
    // cleared under it) cannot be sent; it comes off the form rather than
    // leaving an answer naming a file the server will never own, which would
    // block the submit with a sentence nobody can act on.
    for (const file of form.files.filter((f) => !f.uploaded)) {
      const blob = await store.getBlob(file.id);
      if (!blob) {
        form = (await record(store, id, (f) => dropFile(f, file.id)))!;
        continue;
      }
      await api.upload(form.id, file.fieldKey, file.kind, blob, file.filename, file.id);
      form = (await record(store, id, (f) => ({
        ...f,
        files: f.files.map((x) => (x.id === file.id ? { ...x, uploaded: true } : x)),
      })))!;
    }

    // 3. Removals of photos that had already uploaded.
    for (const fileId of form.removedFileIds) {
      try {
        await api.deleteFile(form.id, fileId);
      } catch (error) {
        // Already gone is the outcome being asked for.
        if (!(error instanceof ApiError && error.status === 404)) throw error;
      }
      form = (await record(store, id, (f) => ({
        ...f,
        removedFileIds: f.removedFileIds.filter((x) => x !== fileId),
      })))!;
    }

    // 4. The answers. The revision is read BEFORE the send and recorded
    // after, so an edit made while the save was in flight stays unsaved and
    // goes on the next run rather than being marked as sent.
    if (form.revision !== form.savedRevision) {
      const sending = form.revision;
      await api.save(form.id, form.answers);
      form = (await record(store, id, (f) => ({ ...f, savedRevision: Math.max(f.savedRevision, sending) })))!;
    }

    // 5. Submit.
    if (form.submitAt !== null) {
      if (form.revision !== form.savedRevision) {
        // Edited between Submit and here: go round again rather than
        // submitting answers the server has not got.
        return runOnce(id, store, api);
      }
      const late = Date.now() - Date.parse(form.submitAt) >= 60_000;
      const result = await api.submit(form.id, late ? form.submitAt : undefined);
      await forgetForm(store, form);
      return { kind: 'sent', submission: result.submission };
    }

    return { kind: 'saved' };
  } catch (error) {
    if (isNetwork(error)) return { kind: 'offline' };
    if (isSignedOut(error)) return { kind: 'signed_out' };
    if (step === 'after-create' && isAlreadySubmitted(error)) {
      // The server holds it as submitted: an earlier submit whose reply was
      // lost, or the same form finished on another device. Either way the
      // record there is final, and this copy has nothing left to add.
      await forgetForm(store, form);
      return { kind: 'sent', submission: null };
    }
    // The server said no. Back to a draft on the phone, with its reasons.
    const problems = submitProblemsFrom(error);
    const message =
      error instanceof ApiError || error instanceof Error
        ? error.message
        : 'Dayboard could not take this form.';
    await record(store, id, (f) => ({
      ...f,
      submitAt: null,
      problem: problems.length > 0 ? null : message,
      problems,
    }));
    return { kind: 'refused' };
  }
}

/** Takes a file off a record and out of its answers. */
export function dropFile(form: LocalForm, fileId: string): LocalForm {
  const answers: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(form.answers)) {
    if (Array.isArray(value) && value.includes(fileId)) {
      const kept = value.filter((v) => v !== fileId);
      if (kept.length > 0) answers[key] = kept;
    } else if (value !== fileId) {
      answers[key] = value;
    }
  }
  const changed = JSON.stringify(answers) !== JSON.stringify(form.answers);
  return {
    ...form,
    answers,
    files: form.files.filter((f) => f.id !== fileId),
    revision: changed ? form.revision + 1 : form.revision,
  };
}

// --- One run at a time, per form -----------------------------------------

const running = new Map<string, Promise<SyncOutcome>>();
const again = new Set<string>();

/**
 * Sends one form, never twice at once. A request arriving mid-run is folded
 * into ONE follow-up run, so a burst of edits costs two runs, not twenty.
 */
export function syncForm(
  id: string,
  deps: { store?: OfflineStore; api?: SyncApi } = {}
): Promise<SyncOutcome> {
  const store = deps.store ?? offlineStore();
  const api = deps.api ?? liveApi;
  const inFlight = running.get(id);
  if (inFlight) {
    // The run in flight picks this up before it settles (the loop below), so
    // its outcome already includes whatever prompted this call.
    again.add(id);
    return inFlight;
  }
  const run = (async () => {
    let outcome = await runOnce(id, store, api);
    // Only a clean run goes round again: after 'offline' or a refusal, an
    // immediate second attempt would meet the same answer.
    while (again.has(id) && outcome.kind === 'saved') {
      again.delete(id);
      outcome = await runOnce(id, store, api);
    }
    again.delete(id);
    return outcome;
  })().finally(() => running.delete(id));
  running.set(id, run);
  return run;
}
